import { z } from "zod";
import type { Database } from "@/integrations/supabase/types";
export type EventoCarrera = Database["public"]["Tables"]["eventos_carrera"]["Row"];
// Spanish fallback for any validation message not written explicitly.
z.setErrorMap((issue, ctx) => {
  if (issue.code === "invalid_type" && issue.received === "undefined")
    return { message: "Falta un dato obligatorio" };
  if (issue.code === "too_small") return { message: "Este dato es demasiado corto" };
  if (issue.code === "too_big") return { message: "Este dato es demasiado largo" };
  if (issue.code === "invalid_string") return { message: "El formato no es válido" };
  if (issue.code === "invalid_enum_value") return { message: "Elige una opción válida" };
  return { message: ctx.defaultError && "Revisa los datos del formulario" };
});
/** First validation message, readable in Spanish, for showing to the user. */
export function primerError(err: unknown) {
  if (err instanceof z.ZodError) return err.issues[0]?.message ?? "Revisa los datos del formulario";
  return err instanceof Error ? err.message : "Revisa los datos del formulario";
}
export const modalidadLabels = {
  corredor: "Corredor",
  andarin: "Andarín",
  acompanante: "Acompañante",
};
/** Lee participantes ya guardados sin validar (las inscripciones antiguas no tienen fecha de nacimiento). */
export function leerParticipantes(json: unknown): Participante[] {
  if (!Array.isArray(json)) return [];
  return json.map((x) => {
    const p = (x ?? {}) as Record<string, unknown>;
    const mod = p.modalidad === "andarin" || p.modalidad === "acompanante" ? p.modalidad : "corredor";
    return {
      nombre: String(p.nombre ?? ""),
      modalidad: mod,
      fecha_nacimiento: typeof p.fecha_nacimiento === "string" ? p.fecha_nacimiento : "",
      grupo: String(p.grupo ?? ""),
      menor: Boolean(p.menor),
      tutor: String(p.tutor ?? ""),
      autorizacion: Boolean(p.autorizacion),
    } as Participante;
  });
}
export const participanteSchema = z
  .object({
    nombre: z.string().trim().min(2, "Escribe el nombre y apellidos de cada persona").max(120, "El nombre es demasiado largo"),
    modalidad: z.enum(["corredor", "andarin", "acompanante"], {
      errorMap: () => ({ message: "Elige la modalidad de cada persona" }),
    }),
    fecha_nacimiento: z
      .string()
      .trim()
      .min(10, "Escribe la fecha de nacimiento de cada persona")
      .max(10, "La fecha de nacimiento no es válida")
      .regex(/^\d{4}-\d{2}-\d{2}$/, "La fecha de nacimiento no es válida"),
    grupo: z.string().trim().max(100, "El nombre del club es demasiado largo").default(""),
    menor: z.boolean(),
    tutor: z.string().trim().max(120).default(""),
    autorizacion: z.boolean().default(false),
  })
  .superRefine((p, ctx) => {
    const [y, m, d] = p.fecha_nacimiento.split("-").map(Number);
    const f = new Date(Date.UTC(y, m - 1, d));
    const hoy = new Date();
    if (
      Number.isNaN(f.getTime()) ||
      f.getUTCFullYear() !== y ||
      f.getUTCMonth() !== m - 1 ||
      f.getUTCDate() !== d ||
      y < 1900 ||
      f > hoy
    ) {
      ctx.addIssue({
        code: "custom",
        message: "La fecha de nacimiento no es válida",
        path: ["fecha_nacimiento"],
      });
    } else if (p.modalidad === "corredor" && edadEn(p.fecha_nacimiento, hoy) < 16) {
      ctx.addIssue({
        code: "custom",
        message: "Los menores de 16 años solo pueden inscribirse como andarines",
        path: ["modalidad"],
      });
    }
    if (p.menor && (p.tutor.length < 3 || !p.autorizacion))
      ctx.addIssue({
        code: "custom",
        message: "Para un menor, escribe el nombre del adulto responsable y marca la autorización",
        path: ["tutor"],
      });
  });
/** Años cumplidos a fecha de referencia (hoy por defecto), a partir de una fecha ISO aaaa-mm-dd. */
export const edadEn = (fecha: string, ref: Date = new Date()) => {
  const [y, m, d] = fecha.split("-").map(Number);
  if (!y || !m || !d) return 0;
  let edad = ref.getFullYear() - y;
  const antes = ref.getMonth() + 1 < m || (ref.getMonth() + 1 === m && ref.getDate() < d);
  if (antes) edad -= 1;
  return Math.max(0, edad);
};
export type Participante = z.infer<typeof participanteSchema>;
export const contactoSchema = z.object({
  nombre: z
    .string()
    .trim()
    .min(2, "Escribe el nombre y apellidos de la persona de contacto")
    .max(120, "El nombre es demasiado largo"),
  email: z
    .string()
    .trim()
    .min(1, "Escribe un correo electrónico para recibir la confirmación")
    .email("El correo electrónico no es válido. Revísalo, por ejemplo: nombre@gmail.com")
    .max(254, "El correo electrónico es demasiado largo")
    .transform((v) => v.toLowerCase()),
  telefono: z
    .string()
    .trim()
    .regex(/^[+\d\s()-]{7,25}$/, "Escribe un teléfono válido, por ejemplo: 600 123 456"),
});
export const inscripcionSchema = z.object({
  idempotencia: z.string().uuid(),
  participantes: z
    .array(participanteSchema)
    .min(1, "Añade al menos una persona")
    .max(30, "Puedes inscribir hasta 30 personas por compra"),
  contacto: contactoSchema,
  acepta: z.literal(true, {
    errorMap: () => ({ message: "Acepta el reglamento y las condiciones para continuar" }),
  }),
  website: z.string().max(0),
});
export const eventoSchema = z
  .object({
    titulo: z.string().trim().min(3).max(180),
    fecha: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).refine(v => !Number.isNaN(Date.parse(v)) && new Date(v).toISOString().slice(0,10) === v, "Fecha inválida"),
    salida: z.string().regex(/^(?:[01]\d|2[0-3]):[0-5]\d$/),
    cierre_meta: z.string().regex(/^(?:[01]\d|2[0-3]):[0-5]\d$/),
    recorrido: z.string().trim().min(3).max(300),
    distancia_km: z.coerce.number().positive().max(200),
    precio_participante: z.coerce.number().positive().max(1000),
    precio_acompanante: z.coerce.number().positive().max(1000),
    organizador: z.string().trim().min(3).max(250),
    responsable_legal: z.string().trim().max(2000),
    privacidad: z.string().trim().max(10000),
    condiciones: z.string().trim().max(10000),
    autorizacion_menores: z.string().trim().max(5000),
    inscripciones_abiertas: z.boolean(),
    validacion_legal: z.boolean(),
  })
  .superRefine((e, ctx) => {
    if (
      (e.inscripciones_abiertas || e.validacion_legal) &&
      [e.responsable_legal, e.privacidad, e.condiciones, e.autorizacion_menores].some(
        (v) => v.length < 20,
      )
    )
      ctx.addIssue({
        code: "custom",
        message:
          "Completa los datos legales, privacidad, condiciones y autorización de menores antes de abrir.",
      });
    if (e.inscripciones_abiertas && !e.validacion_legal)
      ctx.addIssue({
        code: "custom",
        message: "Confirma la revisión legal antes de abrir inscripciones.",
      });
  });
export const euro = (n: number) =>
  new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 2,
  }).format(n);
export const fechaEvento = (fecha: string) =>
  new Intl.DateTimeFormat("es-ES", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "Europe/Madrid",
  }).format(new Date(`${fecha}T12:00:00Z`));
export function totalParticipantes(personas: Participante[], evento: EventoCarrera) {
  return personas.reduce(
    (s, p) =>
      s +
      Math.round(Number(
        p.modalidad === "acompanante" ? evento.precio_acompanante : evento.precio_participante,
      ) * 100),
    0,
  ) / 100;
}
export function abierto(evento: EventoCarrera) {
  return (
    evento.inscripciones_abiertas &&
    evento.validacion_legal &&
    new Intl.DateTimeFormat("sv-SE", { timeZone: "Europe/Madrid" }).format(new Date()) <=
      evento.fecha
  );
}

/** Purchase code derived from the registration UUID: unique and checkable in Gestión. */
export function codigoCompra(id: string) {
  const hex = id.replace(/-/g, "").toUpperCase();
  return `SS26-${hex.slice(0, 4)}-${hex.slice(4, 8)}-${hex.slice(8, 12)}`;
}
